import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, apiRequest, authRequiredEventName, jsonBody } from "../../api/client";
import type { AuthConfig, AuthState } from "../../api/types";
import { useI18n } from "../../i18n";

interface AuthApi extends AuthState {
  config: AuthConfig;
  loading: boolean;
  error: Error | null;
  loginNotice: string;
  logoutPending: boolean;
  login: (username: string, password: string) => Promise<void>;
  bootstrap: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const EMPTY_AUTH: AuthState = { authenticated: false, user: null, bootstrap_required: false };
const DEFAULT_AUTH_CONFIG: AuthConfig = {
  mode: "local",
  login_url: "",
  token_storage_key: "",
  session_revalidate_seconds: 300,
};

interface AuthSnapshot {
  config: AuthConfig;
  state: AuthState;
  platformLoginIssue: "" | "expired" | "access_denied";
  sessionVerified: boolean;
}

async function loadAuthSnapshot(signal?: AbortSignal): Promise<AuthSnapshot> {
  const config = await apiRequest<AuthConfig>("/api/auth/config", { signal });
  const state = await apiRequest<AuthState>("/api/auth/me", { signal });
  if (config.mode !== "im_production_same_origin" || state.authenticated) {
    return { config, state, platformLoginIssue: "", sessionVerified: state.authenticated };
  }
  const token = config.token_storage_key
    ? window.localStorage.getItem(config.token_storage_key)?.trim()
    : "";
  if (!token) return { config, state, platformLoginIssue: "", sessionVerified: false };
  try {
    const result = await apiRequest<{ user: NonNullable<AuthState["user"]> }>("/api/auth/platform", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    return {
      config,
      state: { authenticated: true, user: result.user, bootstrap_required: false },
      platformLoginIssue: "",
      sessionVerified: false,
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      window.localStorage.removeItem(config.token_storage_key);
      return { config, state: EMPTY_AUTH, platformLoginIssue: "expired", sessionVerified: false };
    }
    if (error instanceof ApiError && error.status === 403) {
      return { config, state: EMPTY_AUTH, platformLoginIssue: "access_denied", sessionVerified: false };
    }
    throw error;
  }
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  // 存 key 而不是成品文案，切换语言后已显示的提示也会跟着变。
  const [loginNoticeKey, setLoginNoticeKey] = useState("");
  const authRequiredHandledRef = useRef(false);
  const platformRecoveryAttemptedRef = useRef(false);
  const authQuery = useQuery({
    queryKey: ["auth"],
    queryFn: ({ signal }) => loadAuthSnapshot(signal),
    retry: false,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const snapshot = query.state.data;
      return snapshot?.config.mode === "im_production_same_origin"
        ? snapshot.config.session_revalidate_seconds * 1000
        : false;
    },
  });
  const loginMutation = useMutation({
    mutationFn: (payload: { username: string; password: string; bootstrap: boolean }) =>
      apiRequest<{ user: NonNullable<AuthState["user"]> }>(payload.bootstrap ? "/api/auth/bootstrap" : "/api/auth/login", {
        method: "POST",
        body: jsonBody({ username: payload.username, password: payload.password }),
      }),
    onSuccess: (value) => {
      authRequiredHandledRef.current = false;
      platformRecoveryAttemptedRef.current = false;
      setLoginNoticeKey("");
      queryClient.setQueryData<AuthSnapshot>(["auth"], (current) => ({
        config: current?.config ?? DEFAULT_AUTH_CONFIG,
        state: { authenticated: true, user: value.user, bootstrap_required: false },
        platformLoginIssue: "",
        sessionVerified: true,
      }));
    },
  });
  const config = authQuery.data?.config ?? DEFAULT_AUTH_CONFIG;
  const state = authQuery.data?.state ?? EMPTY_AUTH;
  useEffect(() => {
    if (authQuery.data?.platformLoginIssue === "expired") {
      setLoginNoticeKey("auth.platformSessionExpired");
    } else if (authQuery.data?.platformLoginIssue === "access_denied") {
      setLoginNoticeKey("auth.platformAccessDenied");
    }
    if (state.authenticated) {
      authRequiredHandledRef.current = false;
      if (authQuery.data?.sessionVerified) platformRecoveryAttemptedRef.current = false;
      setLoginNoticeKey("");
    }
  }, [authQuery.data?.platformLoginIssue, authQuery.data?.sessionVerified, state.authenticated]);
  useEffect(() => {
    if (config.mode !== "im_production_same_origin" || !config.token_storage_key) return;
    const refreshForTokenChange = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && event.key === config.token_storage_key) {
        authRequiredHandledRef.current = false;
        platformRecoveryAttemptedRef.current = false;
        void authQuery.refetch();
      }
    };
    window.addEventListener("storage", refreshForTokenChange);
    return () => window.removeEventListener("storage", refreshForTokenChange);
  }, [authQuery, config.mode, config.token_storage_key]);
  const logoutMutation = useMutation({
    mutationFn: () => apiRequest<void>("/api/auth/logout", { method: "POST" }),
    networkMode: "always",
    retry: false,
    onSuccess: () => {
      if (config.mode === "im_production_same_origin" && config.token_storage_key) {
        window.localStorage.removeItem(config.token_storage_key);
      }
      queryClient.clear();
      queryClient.setQueryData<AuthSnapshot>(["auth"], {
        config,
        state: EMPTY_AUTH,
        platformLoginIssue: "",
        sessionVerified: false,
      });
    },
  });

  useEffect(() => {
    const handleAuthRequired = () => {
      if (authRequiredHandledRef.current) return;
      authRequiredHandledRef.current = true;
      setLoginNoticeKey("auth.sessionExpired");
      void queryClient.cancelQueries();
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      queryClient.setQueryData<AuthSnapshot>(["auth"], (current) => ({
        config: current?.config ?? DEFAULT_AUTH_CONFIG,
        state: EMPTY_AUTH,
        platformLoginIssue: "",
        sessionVerified: false,
      }));
      if (
        config.mode === "im_production_same_origin"
        && !platformRecoveryAttemptedRef.current
      ) {
        platformRecoveryAttemptedRef.current = true;
        void authQuery.refetch().finally(() => {
          authRequiredHandledRef.current = false;
        });
      }
    };
    window.addEventListener(authRequiredEventName(), handleAuthRequired);
    return () => window.removeEventListener(authRequiredEventName(), handleAuthRequired);
  }, [authQuery, config.mode, queryClient]);

  const value = useMemo<AuthApi>(() => ({
    ...state,
    config,
    loading: authQuery.isPending,
    error: authQuery.error,
    loginNotice: loginNoticeKey ? t(loginNoticeKey) : "",
    logoutPending: logoutMutation.isPending,
    login: async (username, password) => {
      await loginMutation.mutateAsync({ username, password, bootstrap: false });
    },
    bootstrap: async (username, password) => {
      await loginMutation.mutateAsync({ username, password, bootstrap: true });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    refresh: async () => {
      authRequiredHandledRef.current = false;
      platformRecoveryAttemptedRef.current = false;
      await authQuery.refetch();
    },
  }), [authQuery, config, loginMutation, loginNoticeKey, logoutMutation, state, t]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
